import { KeySet } from '@tableau/data-structures';
import { DialogPositioningBehavior, DialogProps, DialogType, DialogWidget } from '@tableau/widgets';
import * as React from 'react';
import { Messages } from '../../../../gen-src/Localize';
import { ComposeEmailDialogContent, ComposeEmailDialogFooter, TextValidity } from './ComposeEmailDialogContent';
import { ComposeEmailDialogStyles } from './ComposeEmailDialogStyles';

export interface DialogResultParams {
  subjectText: string;
  bodyText: string;
}

export interface ComposeEmailDialogContainerProps {
  readonly selectedUserIds: KeySet;
  readonly dismissDialog: () => void;
  readonly submitResult: (args: DialogResultParams) => void;
}

export interface ComposeEmailDialogContainerState {
  readonly isSubmitting: boolean;
  readonly subjectText: string;
  readonly bodyText: string;
  readonly subjectValidity: TextValidity;
  readonly bodyValidity: TextValidity;
}

function validateText(text: string, maximum: number): TextValidity {
  if (text.length > maximum) {
    return 'overflow';
  }

  if (text.replace(/\s/g, '') === '') {
    return 'empty';
  }

  return 'valid';
}

export class ComposeEmailDialogContainer extends React.Component<ComposeEmailDialogContainerProps, ComposeEmailDialogContainerState> {

  public constructor(props: ComposeEmailDialogContainerProps) {
    super(props);
    this.state = {
      isSubmitting: false,
      subjectText: '',
      bodyText: '',
      subjectValidity: 'initial',
      bodyValidity: 'initial',
    };
  }

  private handleBodyChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const bodyText = event.target.value;

    this.setState((s, _) => {
      const isBodyValid = validateText(bodyText, ComposeEmailDialogStyles.MAXIMUM_BODY_LENGTH);
      return { ...s, bodyText, bodyValidity: isBodyValid };
    });
  }

  private handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const subjectText = e.target.value;
    this.setState((s, _) => {
      const isSubjectValid = validateText(subjectText, ComposeEmailDialogStyles.MAXIMUM_SUBJECT_LENGTH);
      return { ...s, subjectText, subjectValidity: isSubjectValid };
    });
  }

  private invokeSubmit = () => {
    this.setState({ isSubmitting: true }, () => this.props.submitResult({
      bodyText: this.state.bodyText,
      subjectText: this.state.subjectText,
    }));
  }

  // TODO: Once we have the correct ReactModalService methods exposed via UIActions, use them so that we
  // get the correct styled props without having to declare them all by hand.
  private dialogProps(): DialogProps {
    return {
      title: Messages.EmailDialogTitle(),
      dialogWidth: ComposeEmailDialogStyles.DIALOG_WIDTH,
      testId: ComposeEmailDialogStyles.TEST_ID('dialog'),
      isModal: true,
      handleGlassClick: this.props.dismissDialog,
      type: DialogType.NormalWithTitleBar,
      positioningBehavior: DialogPositioningBehavior.Centered,
      shouldAdjustPositionOnWindowResize: true,
      onCloseRequest: this.props.dismissDialog,
      showCloseButton: false,
      showFooter: true,
      footerContent: (
        <ComposeEmailDialogFooter
          dismissDialog={this.props.dismissDialog}
          invokeSubmit={this.invokeSubmit}
          isSubmitting={this.state.isSubmitting}
          isValid={this.state.bodyValidity === 'valid' && this.state.subjectValidity === 'valid'}
        />
      ),
    };
  }

  public render(): JSX.Element {
    return (
      <DialogWidget {...this.dialogProps()}>
        <ComposeEmailDialogContent
          subjectValidity={this.state.subjectValidity}
          bodyValidity={this.state.bodyValidity}
          selectedUserIds={this.props.selectedUserIds}
          bodyText={this.state.bodyText}
          subjectText={this.state.subjectText}
          handleBodyChange={this.handleBodyChange}
          handleSubjectChange={this.handleSubjectChange}
        />
      </DialogWidget>
    );
  }
}
